import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
} from "@mui/material";
import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import useMediaQuery from "@mui/material/useMediaQuery";
import {PrintPreviewData} from "../../hooks/useFloorplanPrint";
import {
    applyPrintTableConfig,
    buildPrintHTML,
    clearPersistedPrintConfig,
    createDefaultPersistedPrintConfig,
    Orientation,
    PrintTableConfig,
    printViaIframe,
    readPersistedPrintConfig,
    renderPrintTableHTML,
    writePersistedPrintConfig
} from "../../utils/print-utils";
import PrintPreviewCanvas, {PrintPreviewCanvasHandle} from "./PrintPreviewCanvas";
import PrintTablePanel from "./PrintTablePanel";
import {DEFAULT_PRINT_PREVIEW_STATE, PrintPreviewCanvasState} from "../../utils/print-preview.utils";

interface Props {
    open: boolean;
    previewData: PrintPreviewData | null;
    onCancel: () => void;
}

const FloorplanPrintPreviewDialog: React.FC<Props> = ({
                                                          open,
                                                          previewData,
                                                          onCancel,
                                                      }) => {
    const {t} = useTranslation();
    const isMobile = useMediaQuery('(max-width:900px)');
    const previewCanvasRef = useRef<PrintPreviewCanvasHandle | null>(null);
    const livePreviewStateRef = useRef<PrintPreviewCanvasState>(DEFAULT_PRINT_PREVIEW_STATE);
    const [initialPreviewState, setInitialPreviewState] = useState<PrintPreviewCanvasState>(DEFAULT_PRINT_PREVIEW_STATE);
    const [previewStateVersion, setPreviewStateVersion] = useState(0);
    const [printOrientation, setPrintOrientation] = useState<Orientation>('portrait');
    const [tableConfig, setTableConfig] = useState<PrintTableConfig>(createDefaultPersistedPrintConfig('portrait').tableConfig);

    const createDefaultConfig = (orientation: Orientation) => createDefaultPersistedPrintConfig(orientation);

    const applyDialogConfig = (config: {
        printOrientation: Orientation;
        previewState: PrintPreviewCanvasState;
        tableConfig: PrintTableConfig;
    }) => {
        setPrintOrientation(config.printOrientation);
        setTableConfig(config.tableConfig);
        setInitialPreviewState(config.previewState);
        livePreviewStateRef.current = config.previewState;
        setPreviewStateVersion((current) => current + 1);
    };

    useEffect(() => {
        if (!open) {
            return;
        }

        const defaultConfig = createDefaultConfig(previewData?.orientation ?? 'portrait');
        applyDialogConfig(readPersistedPrintConfig() ?? defaultConfig);
    }, [open, previewData?.dataUrl, previewData?.orientation]);

    const handleResetPreview = () => {
        previewCanvasRef.current?.reset();
        setInitialPreviewState(DEFAULT_PRINT_PREVIEW_STATE);
        livePreviewStateRef.current = DEFAULT_PRINT_PREVIEW_STATE;
        setPreviewStateVersion((current) => current + 1);
    };

    const handleResetToDefaults = () => {
        const defaultConfig = createDefaultConfig(previewData?.orientation ?? 'portrait');
        clearPersistedPrintConfig();
        applyDialogConfig(defaultConfig);
    };

    const handleClose = () => {
        handleResetPreview();
        setTableConfig(createDefaultConfig(previewData?.orientation ?? 'portrait').tableConfig);
        onCancel();
    };

    const handleConfirmPrint = () => {
        if (!previewData) {
            return;
        }

        const currentPreviewState = previewCanvasRef.current?.getState() ?? livePreviewStateRef.current;

        writePersistedPrintConfig({
            printOrientation,
            previewState: currentPreviewState,
            tableConfig,
        });

        const tableHtml = renderPrintTableHTML(
            applyPrintTableConfig(previewData.reservationRows, tableConfig),
            tableConfig,
            previewData.labels,
        );

        const html = buildPrintHTML({
            orientation: printOrientation,
            dataUrl: previewData.dataUrl,
            imageWidth: previewData.imageWidth,
            imageHeight: previewData.imageHeight,
            printTitle: previewData.printTitle,
            restaurantName: previewData.restaurantNameEscaped,
            formattedDate: previewData.formattedDate,
            timeInfo: previewData.timeInfo,
            totalReservations: previewData.totalReservations,
            totalGuests: previewData.totalGuests,
            timeframeRange: previewData.timeframeRange,
            tableHtml,
            labels: previewData.labels,
            imageScale: currentPreviewState.printScale,
            imageOffset: currentPreviewState.imageOffset,
            imageRotation: currentPreviewState.imageRotation,
        });

        printViaIframe(html, previewData.printTitle);
        handleClose();
    };

    const previewRows = previewData
        ? applyPrintTableConfig(previewData.reservationRows, tableConfig)
        : [];

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth fullScreen={isMobile}>
            <DialogTitle
                sx={{px: {xs: 2, sm: 3}, pt: {xs: 2, sm: 2.5}, pb: {xs: 1, sm: 1.5}}}>{t('Print Preview')}</DialogTitle>
            <DialogContent sx={{px: {xs: 1.5, sm: 3}, pb: {xs: 1.5, sm: 2}}}>
                <Stack
                    direction={{xs: 'column', lg: 'row'}}
                    spacing={2}
                    sx={{mt: 1, mb: 3, alignItems: {lg: 'center'}}}
                >
                    <Alert severity="info" variant="outlined" sx={{flex: 1}}>
                        {t('The print will include reservations for {{time}}', {
                            time: previewData
                                ? `${previewData.formattedDate}${previewData.timeInfo ? `, ${previewData.timeInfo}` : ''}`
                                : '',
                        })}
                    </Alert>

                    <FormControl size="small" sx={{minWidth: {xs: '100%', sm: 220}}}>
                        <InputLabel>{t('Print orientation')}</InputLabel>
                        <Select
                            label={t('Print orientation')}
                            value={printOrientation}
                            onChange={(event) => setPrintOrientation(event.target.value as Orientation)}
                        >
                            <MenuItem value="portrait">{t('Portrait')}</MenuItem>
                            <MenuItem value="landscape">{t('Landscape')}</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>

                <Stack
                    direction={{xs: 'column', lg: 'row'}}
                    spacing={3}
                    sx={{alignItems: 'stretch'}}
                >
                    <Box sx={{flex: {xs: '1 1 auto', lg: '1 1 33.333%'}, minWidth: 0}}>
                        <PrintPreviewCanvas
                            ref={previewCanvasRef}
                            open={open}
                            pageOrientation={printOrientation}
                            previewData={previewData}
                            initialState={initialPreviewState}
                            initialStateVersion={previewStateVersion}
                            onStateChange={(state) => {
                                livePreviewStateRef.current = state;
                            }}
                        />
                    </Box>

                    <Box sx={{flex: {xs: '1 1 auto', lg: '1 1 66.666%'}, minWidth: 0}}>
                        <PrintTablePanel
                            labels={previewData?.labels ?? {
                                floorplan: '',
                                date: '',
                                time: '',
                                todaysReservations: '',
                                todaysGuests: '',
                                timeframe: '',
                                guestRoom: '',
                                guest: '',
                                status: '',
                                nationality: '',
                                stayCount: '',
                                allergiesDiets: '',
                                table: '',
                                start: '',
                                end: '',
                                guests: '',
                                total: '',
                                ageCategories: '',
                                aliasAgeCategories: '',
                                notes: '',
                                stay: '',
                                room: '',
                                roomNumber: '',
                                rateCode: '',
                            }}
                            config={tableConfig}
                            rows={previewRows}
                            onConfigChange={setTableConfig}
                            onReset={handleResetToDefaults}
                        />
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: {xs: 1.5, sm: 3}, pb: {xs: 1.5, sm: 2}, pt: 1}}>
                <Button onClick={handleClose}>{t('Cancel')}</Button>
                <Button variant="contained" onClick={handleConfirmPrint}>{t('Print')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default FloorplanPrintPreviewDialog;
